import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { createMemo, For, Show } from "solid-js";
import type { ContentFitnessOrigin } from "../../enums/scan";
import { SeverityBadge } from "../../ui/Badge";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import {
  type DimensionAssessment,
  issueContributedToDimensionScore,
  pageDimensionScore,
  subcriterionLabel,
} from "./contentFitnessDimensionModel";
import { clientIssueTitle } from "./issueText";
import { pageDetailHref } from "./pageDetailNavigation";
import { EmptyState } from "./ScanContentFitnessParts";
import type { OverviewPage } from "./scanModel";

type Site = OverviewResponse["site"];

export function AffectedPages(props: {
  assessedPages: OverviewPage[];
  loading: boolean;
  websiteId: string;
  site: Site;
  assessments: DimensionAssessment[];
  dimensionKey: string;
  linkedIssueCountForPage: (page: OverviewPage) => number;
  origin: ContentFitnessOrigin;
}) {
  return (
    <section class="scan-panel">
      <div class="scan-section-head">
        <div>
          <span class="scan-kicker">Affected pages</span>
          <h2>Pages contributing to this dimension</h2>
        </div>
      </div>
      <Show
        when={props.assessedPages.length > 0}
        fallback={
          <EmptyState
            loading={props.loading}
            text="No pages have persisted assessments for this dimension yet."
          />
        }
      >
        <div class="scan-issue-list">
          <For each={props.assessedPages}>
            {(page) => (
              <a
                class="scan-issue-row"
                href={pageDetailHref(props.websiteId, page.id, {
                  source: "content-fitness",
                  dimensionKey: props.dimensionKey,
                  dimensionOrigin: props.origin,
                })}
              >
                <span>
                  <strong>{pageDisplayLabel(page, props.site)}</strong>
                  <small class="mono">{rawPagePath(page)}</small>
                </span>
                <span class="scan-assessment-score">
                  <strong class="mono">
                    {pageDimensionScore(
                      props.assessments,
                      page.id,
                      props.dimensionKey,
                    ) ?? "--"}
                  </strong>
                  <small>
                    {props.linkedIssueCountForPage(page)} linked issue
                    {props.linkedIssueCountForPage(page) === 1 ? "" : "s"}
                  </small>
                </span>
              </a>
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

export function LinkedIssues(props: {
  issues: Issue[];
  assessments: DimensionAssessment[];
  websiteId: string;
  dimensionKey: string;
  origin: ContentFitnessOrigin;
}) {
  const scoreContributing = createMemo(() =>
    props.issues.filter((issue) =>
      issueContributedToDimensionScore(issue, props.assessments),
    ),
  );
  const related = createMemo(() =>
    props.issues.filter(
      (issue) => !issueContributedToDimensionScore(issue, props.assessments),
    ),
  );
  return (
    <section class="scan-panel">
      <div class="scan-section-head">
        <div>
          <span class="scan-kicker">Issue context</span>
          <h2>Open issues tied to this dimension</h2>
        </div>
      </div>
      <Show
        when={props.issues.length > 0}
        fallback={
          <p class="signal-muted">
            No open issues currently declare a primary subcriterion in this
            dimension.
          </p>
        }
      >
        <Show when={scoreContributing().length > 0}>
          <div class="scan-related-issues-group">
            <strong>Issues linked to scored criteria</strong>
            <IssueRows
              issues={scoreContributing()}
              websiteId={props.websiteId}
              dimensionKey={props.dimensionKey}
              origin={props.origin}
            />
          </div>
        </Show>
        <Show when={related().length > 0}>
          <div class="scan-related-issues-group is-context-only">
            <strong>Related issues</strong>
            <p class="signal-muted">
              These issues are associated with this dimension but did not
              contribute to the current score.
            </p>
            <IssueRows
              issues={related()}
              websiteId={props.websiteId}
              dimensionKey={props.dimensionKey}
              origin={props.origin}
            />
          </div>
        </Show>
      </Show>
    </section>
  );
}

function IssueRows(props: {
  issues: Issue[];
  websiteId: string;
  dimensionKey: string;
  origin: ContentFitnessOrigin;
}) {
  return (
    <div class="scan-issue-list">
      <For each={props.issues}>
        {(issue) => (
          <a
            class="scan-issue-row"
            href={`/w/${props.websiteId}/scan/issues/${issue.id}?from=content-fitness&dimension=${encodeURIComponent(props.dimensionKey)}&dimensionFrom=${props.origin}`}
          >
            <SeverityBadge severity={issue.severity} />
            <span>
              <strong>{clientIssueTitle(issue)}</strong>
              <small>
                {issue.pages[0]?.path ?? "No page path"} /{" "}
                {subcriterionLabel(issue.primarySubcriterionKey)}
              </small>
            </span>
          </a>
        )}
      </For>
    </div>
  );
}
